<template>
  <Layout>
    <div class="terms-page">
      <!-- Hero Section -->
      <section class="terms-hero bg-gradient-primary text-white py-5">
        <div class="container">
          <div class="row align-items-center">
            <div class="col-lg-8 mx-auto text-center">
              <h1 class="display-4 font-weight-bold mb-3">Terms and Conditions</h1>
              <p class="lead mb-4">Effective Date: August 7, 2025 | Version: 2.1</p>
              <div class="d-flex justify-content-center">
                <b-button variant="light" class="mr-3" @click="scrollToDetails">
                  <b-icon icon="arrow-down" class="mr-2"></b-icon>
                  View Terms
                </b-button>
                <b-button variant="outline-light" @click="downloadTerms">
                  <b-icon icon="download" class="mr-2"></b-icon>
                  Download PDF
                </b-button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Main Content -->
      <div class="container my-5">
        <div class="row">
          <div class="col-lg-12 mx-auto">
            <!-- Introduction -->
            <b-card no-body class="border-0 shadow-sm mb-4">
              <b-card-body>
                <div class="d-flex align-items-center mb-4">
                  <div class="icon-circle bg-primary text-white mr-3">
                    <i class="mdi mdi-file-document" style="font-size: 1.5rem;"></i>
                  </div>
                  <h3 class="mb-0">OVVURA – Unlock Radiance!</h3>
                </div>
                <p>OVVURA PORTAL L.L.C, the registered trade license, owns, maintains and operates the https://ovvura.ae/ Website and mobile app (together, the "Platform"). United Arab Emirates is our country of domicile. By accessing or using the Platform, you agree to these Terms and Conditions. If you do not agree, please do not use the Platform.</p>
              </b-card-body>
            </b-card>

            <!-- Country of Domicile -->
            <b-card no-body class="border-0 shadow-sm mb-4" id="domicile">
              <b-card-header class="bg-light">
                <h4 class="mb-0">1. Country of Domicile and Governing Law</h4>
              </b-card-header>
              <b-card-body>
                <div class="d-flex align-items-start mb-3">
                  <img src="@/assets/images/uae-flag.png" alt="UAE Flag" class="img-fluid rounded mr-4" style="max-width: 120px;">
                  <div>
                    <p>All purchases, disputes or claims arising out of or in connection with this Platform shall be governed by and construed in accordance with the laws of the United Arab Emirates. Any legal action or proceeding must be brought solely in the courts of the UAE.</p>
                  </div>
                </div>
              </b-card-body>
            </b-card>

            <!-- General Information -->
            <b-card no-body class="border-0 shadow-sm mb-4" id="general-info">
              <b-card-header class="bg-light">
                <h4 class="mb-0">2. General Information</h4>
              </b-card-header>
              <b-card-body>
                <h5>2.1 Platform Overview</h5>
                <p>OVVURA connects you with beauty, healthcare, and wellness services, and offers a curated e-commerce store for products.</p>
                
                <h5 class="mt-4">2.2 Eligibility</h5>
                <div class="d-flex align-items-start">
                  <i class="mdi mdi-account-alert text-primary mr-3 mt-1" style="font-size: 1.5rem;"></i>
                  <div>
                    <p>Users must be at least 18 years old. Minors under 18 years of age may not register as users or transact on the Platform.</p>
                  </div>
                </div>
                
                <h5 class="mt-4">2.3 Sanctions Compliance</h5>
                <p>Trading, booking or fulfillment of any services or products will not be provided to countries, entities or individuals subject to OFAC or other applicable international sanctions.</p>
                
                <h5 class="mt-4">2.4 Service Providers</h5>
                <p>Service Providers are independent contractors. OVVURA is not liable for their performance, products or any professional advice given.</p>
              </b-card-body>
            </b-card>

            <!-- Account Registration -->
            <b-card no-body class="border-0 shadow-sm mb-4" id="account-registration">
              <b-card-header class="bg-light">
                <h4 class="mb-0">3. Account Registration</h4>
              </b-card-header>
              <b-card-body>
                <h5>3.1 Account Creation</h5>
                <div class="d-flex align-items-start mb-3">
                  <i class="mdi mdi-account-plus text-primary mr-3 mt-1" style="font-size: 1.5rem;"></i>
                  <div>
                    <p>You agree to provide accurate, complete and up-to-date information. You may not share your credentials with anyone.</p>
                  </div>
                </div>
                
                <h5>3.2 Account Security</h5>
                <div class="d-flex align-items-start">
                  <i class="mdi mdi-shield-lock text-primary mr-3 mt-1" style="font-size: 1.5rem;"></i>
                  <div>
                    <p>You are responsible for maintaining the confidentiality of your account credentials. Notify OVVURA immediately of any unauthorized account use.</p>
                  </div>
                </div>
              </b-card-body>
            </b-card>

            <!-- Booking and Purchases -->
            <b-card no-body class="border-0 shadow-sm mb-4" id="booking-purchases">
              <b-card-header class="bg-light">
                <h4 class="mb-0">4. Booking, Services and E-commerce Purchases</h4>
              </b-card-header>
              <b-card-body>
                <h5>4.1 Booking Services</h5>
                <p>All bookings are with third-party providers; their terms and conditions may also apply.</p>
                
                <h5 class="mt-4">4.2 Product Selection & Availability</h5>
                <p>Products are subject to availability and may change without notice.</p>
                
                <h5 class="mt-4">4.3 Payment Methods and Currencies</h5>
                <div class="d-flex align-items-start mb-3">
                  <!-- <img src="@/assets/images/payment-methods.png" alt="Payment Methods" class="img-fluid rounded mr-4" style="max-width: 200px;"> -->
                  <div>
                    <p>We accept Visa, MasterCard, UnionPay and American Express (debit and credit). Transactions are processed in AED. The price and currency displayed at checkout will be the same as printed on your Transaction Receipt, and the amount charged to your card will appear in your card's currency.</p>
                  </div>
                </div>
                
                <h5 class="mt-4">4.4 Transaction Receipts</h5>
                <p>Cardholders must retain copies of all transaction records and adhere to applicable merchant policies and rules.</p>
                
                <h5 class="mt-4">4.5 Refund & Cancellation</h5>
                <ul class="feature-list">
                  <li>
                    <i class="mdi mdi-calendar-remove text-primary mr-2"></i>
                    Customers may cancel services within 24 hours without fee; thereafter, fees may apply.
                  </li>
                  <li>
                    <i class="mdi mdi-cash-refund text-primary mr-2"></i>
                    Refunds are issued to the original payment method within 10–45 days, subject to bank processing times.
                  </li>
                  <li>
                    <i class="mdi mdi-alert-circle text-primary mr-2"></i>
                    "No Refund / No Cancellation" items are clearly marked before purchase and are final sale.
                  </li>
                </ul>
                
                <h5 class="mt-4">4.6 Product Delivery Policy</h5>
                <div class="delivery-policy-card p-4 mb-3">
                  <div class="row">
                    <div class="col-md-4">
                      <div class="d-flex align-items-center mb-3">
                        <i class="mdi mdi-truck-delivery text-primary mr-2"></i>
                        <h6 class="mb-0">Shipping Process</h6>
                      </div>
                      <p class="small">Orders are processed within 1 business day after payment confirmation and dispatched via our logistics partners.</p>
                    </div>
                    <div class="col-md-4">
                      <div class="d-flex align-items-center mb-3">
                        <i class="mdi mdi-currency-usd text-primary mr-2"></i>
                        <h6 class="mb-0">Delivery Fees</h6>
                      </div>
                      <p class="small">Fees are calculated at checkout based on weight and destination. Free standard delivery applies to UAE orders over AED 200.</p>
                    </div>
                    <div class="col-md-4">
                      <div class="d-flex align-items-center mb-3">
                        <i class="mdi mdi-clock text-primary mr-2"></i>
                        <h6 class="mb-0">Delivery Time</h6>
                      </div>
                      <p class="small">Local (UAE): 2–5 business days. International: 10–20 business days, subject to customs clearance.</p>
                    </div>
                  </div>
                </div>
                
                <h5 class="mt-4">4.7 Payment Confirmation – Services</h5>
                <p>For all service bookings, you will receive payment confirmation by email and/or SMS to the address or number provided during checkout. Confirmation is normally sent within 1 hour of successful payment. If you do not receive it within this timeframe, please contact support.</p>
                
                <h5 class="mt-4">4.8 Return, Refund and Cancellation Policy</h5>
                <div class="refund-policy-grid">
                  <div class="refund-item">
                    <div class="refund-icon bg-danger text-white">
                      <i class="mdi mdi-package-variant"></i>
                    </div>
                    <h6>Wrong Product Sent</h6>
                    <p>Notify us within 2 days of delivery. We will arrange return at our cost and ship the correct item.</p>
                  </div>
                  <div class="refund-item">
                    <div class="refund-icon bg-warning text-white">
                      <i class="mdi mdi-alert"></i>
                    </div>
                    <h6>Defective Product</h6>
                    <p>Report defects within 2 days of receipt. We will inspect and offer a replacement or refund.</p>
                  </div>
                  <div class="refund-item">
                    <div class="refund-icon bg-info text-white">
                      <i class="mdi mdi-package-variant-closed"></i>
                    </div>
                    <h6>Damaged in Shipping</h6>
                    <p>Document damage with photos and notify us within 2 days. We will arrange return and refund or replacement.</p>
                  </div>
                  <div class="refund-item">
                    <div class="refund-icon bg-secondary text-white">
                      <i class="mdi mdi-shield-alert"></i>
                    </div>
                    <h6>Tampered Product</h6>
                    <p>If packaging appears tampered, do not use the product and inform us immediately for return instructions.</p>
                  </div>
                </div>
                <div class="mt-4">
                  <p><strong>Refund Processing:</strong> Refunds will be credited to the original payment method and processed within 10–45 days, depending on your card issuer's policies.</p>
                  <p><strong>Cancellation Policy:</strong> You may cancel in-stock orders before dispatch with no fee. Customized or final-sale items cannot be canceled. Cancellation requests must be submitted via email or through your account within 24 hours of purchase. Merchant will not pass any debit/credit card details to third parties.</p>
                  <p>Items marked "No Refund / No Cancellation" are final sale. This is clearly indicated on the product page before purchase to prevent misunderstandings.</p>
                </div>
              </b-card-body>
            </b-card>

            <!-- Other Sections -->
            <div class="other-sections-grid">
              <b-card no-body class="border-0 shadow-sm h-100">
                <b-card-header class="bg-light">
                  <h5 class="mb-0">5. User Conduct</h5>
                </b-card-header>
                <b-card-body>
                  <p>You agree not to misuse the Platform, post false information, or infringe on others' rights.</p>
                </b-card-body>
              </b-card>

              <b-card no-body class="border-0 shadow-sm h-100">
                <b-card-header class="bg-light">
                  <h5 class="mb-0">6. Privacy and Data Protection</h5>
                </b-card-header>
                <b-card-body>
                  <p>Personal data is handled per our Privacy Policy. We do not store, rent, sell or lease your payment details to third parties.</p>
                </b-card-body>
              </b-card>

              <b-card no-body class="border-0 shadow-sm h-100">
                <b-card-header class="bg-light">
                  <h5 class="mb-0">7. Disclaimers and Liability</h5>
                </b-card-header>
                <b-card-body>
                  <p>Use of the Platform is at your own risk. OVVURA's liability is limited to the maximum extent permitted by law.</p>
                </b-card-body>
              </b-card>

              <b-card no-body class="border-0 shadow-sm h-100">
                <b-card-header class="bg-light">
                  <h5 class="mb-0">8. Indemnity</h5>
                </b-card-header>
                <b-card-body>
                  <p>You agree to indemnify and hold OVVURA harmless against any claims arising from your use of the Platform.</p>
                </b-card-body>
              </b-card>

              <b-card no-body class="border-0 shadow-sm h-100">
                <b-card-header class="bg-light">
                  <h5 class="mb-0">9. Termination</h5>
                </b-card-header>
                <b-card-body>
                  <p>OVVURA may suspend or terminate your account for policy violations or inactivity.</p>
                </b-card-body>
              </b-card>

              <b-card no-body class="border-0 shadow-sm h-100">
                <b-card-header class="bg-light">
                  <h5 class="mb-0">10. Changes to Terms</h5>
                </b-card-header>
                <b-card-body>
                  <p>We may update these Terms. Material changes will be posted with a new effective date.</p>
                </b-card-body>
              </b-card>
            </div>

            <!-- Contact -->
            <b-card no-body class="border-0 shadow-sm mt-4">
              <b-card-body class="text-center bg-light">
                <h4 class="mb-4">Contact Us</h4>
                <div class="contact-methods">
                  <div class="contact-method mb-3">
                    <i class="mdi mdi-email text-primary mr-2"></i>
                    <span>Email: <a href="mailto:support@ovvura.ae">support@ovvura.ae</a></span>
                  </div>
                  <div class="contact-method mb-3">
                    <i class="mdi mdi-phone text-primary mr-2"></i>
                    <span>Phone: <a href="tel:+971525495829">+971 52 549 5829</a></span>
                  </div>
                  <div class="contact-method">
                    <i class="mdi mdi-web text-primary mr-2"></i>
                    <span>Website: <a href="https://www.ovvura.ae" target="_blank">https://www.ovvura.ae</a></span>
                  </div>
                </div>
              </b-card-body>
            </b-card>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<script>
import Layout from "../../layouts/main";

export default {
  components: { Layout },
  methods: {
    scrollToDetails() {
      const element = document.getElementById('domicile');
      element.scrollIntoView({ behavior: 'smooth' });
    },
    downloadTerms() {
      // In a real implementation, this would trigger a PDF download
      this.$bvToast.toast('Terms and Conditions PDF download would start here', {
        title: 'Download',
        variant: 'info',
        solid: true
      });
    }
  }
}
</script>

<style scoped>
.terms-page {
  overflow-x: hidden;
}

.terms-hero {
  background: linear-gradient(135deg, #6e8efb, #a777e3);
  padding: 5rem 0;
}

.icon-circle {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.feature-list {
  list-style: none;
  padding-left: 0;
}

.feature-list li {
  padding: 8px 0;
  display: flex;
  align-items: center;
}

.delivery-policy-card {
  background-color: #f8f9fa;
  border-radius: 10px;
}

.refund-policy-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 20px;
  margin: 20px 0;
}

.refund-item {
  background: white;
  padding: 20px;
  border-radius: 10px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.05);
  text-align: center;
}

.refund-icon {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 15px;
  font-size: 1.5rem;
}

.other-sections-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
  margin: 30px 0;
}

.contact-method {
  display: flex;
  align-items: center;
  justify-content: center;
}

@media (max-width: 768px) {
  .terms-hero {
    padding: 3rem 0;
  }
  
  .terms-hero .btn {
    width: 100%;
    margin-bottom: 10px;
  }
  
  .other-sections-grid {
    grid-template-columns: 1fr;
  }
  
  .refund-policy-grid {
    grid-template-columns: 1fr;
  }
}
</style>